'use client';

import { c as _c } from "react/compiler-runtime";
import { animate, useReducedMotion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '@/app/components/layouts/container';
import { AppImage } from '@/app/components/media/app-image';
import { BG_CORONA_IMAGE_SRC } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/fallbackConfig';
import { IntroVideo } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/intro-engine-reveal/intro-video';
import { type IntroEngineRevealProps } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/intro-engine-reveal/types';
import { useSectionExit } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/useSectionExit';
import { hasPlayableData, playVideoFromGesture, primeVideo } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/autoplay';
import { applyEngineFrameOffset, ENGINE_INTRO_FRAME, ENGINE_SETTLE_DELAY_SECONDS, ENGINE_SETTLE_EASE, ENGINE_SETTLE_SECONDS, getEngineFrameOffset } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/engineFrameOffset';
import { useHeroClipQueueEntry } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/preload-queue/clipQueue';
import { useEngineAnchor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEngineAnchor';
import { useEnginePointerParallax } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEnginePointerParallax';
import { useHeroPlayUnlock } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useHeroPlayUnlock';
import { cancelClipPreload, isClipResident } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoPreload';
import { SECTION_VIDEO_BOX_CLASSNAME } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/styles';
import { SPARTAN_ENGINE_HERO_TRACKING } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/tracking';
import { SECTION_ATMOSPHERE_FADE_OUT_SECONDS, SECTION_EXIT_SECONDS, SECTION_FADE_SECONDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';
import styles from '@/app/lcv/components/sections/spartan/engine/styles.module.css';
import { EngineSpecCard } from '@/app/lcv/components/sections/spartan/shared/engine-spec-card';
import { ENGINE_INTRO_SECTION_DATA } from '@/app/lcv/contents/spartan/engine/hero.data';
import { ENGINE_IMAGE_SRC_45_ANGLE, ENGINE_LOGO_SRC } from '@/app/lcv/contents/spartan/engine/shared/22biturbo-images.data';
import { ENGINE_SPEC } from '@/app/lcv/contents/spartan/engine/shared/22biturbo-specifications.data';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { buildHtmlMarkup } from '@/app/utils/html/html';
import { safeCall } from '@/app/utils/safeCall';
const INTRO_VIDEO_END_PROGRESS = 0.95;
const SPEC_VALUE_DELAY_MS = 150;
const TextDescription = t0 => {
  const $ = _c(23);
  const {
    title,
    description,
    variant,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("w-full text-white", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== variant) {
    t2 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.introText(variant)
    });
    $[2] = variant;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = buildHtmlMarkup(title);
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== variant) {
    t4 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.introTitle(variant)
    });
    $[6] = variant;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <p className="text-sub-h2 font-noto-bold lg:mb-1" dangerouslySetInnerHTML={t3} {...t4} />;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== description) {
    t6 = buildHtmlMarkup(description);
    $[11] = description;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== variant) {
    t7 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.introDescription(variant)
    });
    $[13] = variant;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <p className="text-body-1" dangerouslySetInnerHTML={t6} {...t7} />;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t5 || $[21] !== t8) {
    t9 = <div className={t1} {...t2}>{t5}{t8}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
export const IntroEngineReveal = t0 => {
  const $ = _c(113);
  const {
    shouldRenderFallback: t1,
    phase,
    onExitEnd,
    isActive,
    playMode,
    onIntroVideoEnded,
    onAnimationEnd,
    onFloatCycle,
    onVideoReady,
    onVideoPlaying,
    onVideoError,
    onVideoBlocked,
    registerPlayUnlock,
    onImageLoaded,
    readyToPlay: t2,
    canvasBoxRef,
    introFloatAnimationRef,
    engineAnchorRef,
    engineAuraVisibilityRef
  } = t0;
  const shouldRenderFallback = t1 === undefined ? false : t1;
  const readyToPlay = t2 === undefined ? true : t2;
  const isReducedMotion = useReducedMotion();
  const videoElRef = useRef(null);
  const engineSlotRef = useRef(null);
  const engineRef = useRef(null);
  const enginePointerRef = useRef(null);
  const stageRef = useRef(null);
  const videoBoxRef = useRef(null);
  const videoEndedRef = useRef(onIntroVideoEnded);
  const animationEndRef = useRef(onAnimationEnd);
  const shouldPlayVideo = playMode === "full";
  const introClipSrc = ENGINE_INTRO_SECTION_DATA.videoSources.src.desktop;
  useHeroClipQueueEntry("introMain", introClipSrc, shouldPlayVideo);
  let t3;
  let t4;
  if ($[0] !== shouldPlayVideo) {
    t3 = () => {
      if (!shouldPlayVideo) {
        cancelClipPreload(introClipSrc);
      }
    };
    t4 = [shouldPlayVideo, introClipSrc];
    $[0] = shouldPlayVideo;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  const [isVideoIntroReady, setIsVideoIntroReady] = useState(false);
  let t5;
  if ($[3] !== shouldPlayVideo) {
    t5 = () => !shouldPlayVideo;
    $[3] = shouldPlayVideo;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [startSettleAnimation, setStartSettleAnimation] = useState(t5);
  let t6;
  if ($[5] !== shouldPlayVideo) {
    t6 = () => !shouldPlayVideo;
    $[5] = shouldPlayVideo;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [isEngineSettled, setIsEngineSettled] = useState(t6);
  const [isSpecValueReady, setIsSpecValueReady] = useState(false);
  let t7;
  let t8;
  if ($[7] !== introFloatAnimationRef || $[8] !== isActive || $[9] !== isEngineSettled || $[10] !== isReducedMotion) {
    t7 = () => {
      if (!introFloatAnimationRef || !isEngineSettled || !isActive || isReducedMotion) {
        return;
      }
      const animation = engineRef.current?.getAnimations().find(_temp) ?? null;
      introFloatAnimationRef.current = animation;
      return () => {
        if (introFloatAnimationRef.current === animation) {
          introFloatAnimationRef.current = null;
        }
      };
    };
    t8 = [introFloatAnimationRef, isEngineSettled, isActive, isReducedMotion];
    $[7] = introFloatAnimationRef;
    $[8] = isActive;
    $[9] = isEngineSettled;
    $[10] = isReducedMotion;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== isEngineSettled || $[14] !== isReducedMotion) {
    t9 = isReducedMotion ? undefined : cn("property-opacity-translate ease-bounce transform-gpu duration-500", {
      "translate-y-4 opacity-0": !isEngineSettled
    });
    $[13] = isEngineSettled;
    $[14] = isReducedMotion;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const animateInSettledUiClassName = t9;
  const isOnScreen = isActive || phase === "exiting";
  const isAuraLit = isOnScreen && startSettleAnimation;
  const auraWriteRef = useRef(engineAuraVisibilityRef);
  let t10;
  if ($[16] !== onExitEnd || $[17] !== phase) {
    t10 = {
      phase,
      onExitEnd,
      durationSeconds: SECTION_EXIT_SECONDS
    };
    $[16] = onExitEnd;
    $[17] = phase;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  useSectionExit(t10);
  let t11;
  if ($[19] !== canvasBoxRef || $[20] !== engineAnchorRef || $[21] !== isOnScreen) {
    t11 = {
      elementRef: engineSlotRef,
      canvasBoxRef,
      anchorRef: engineAnchorRef,
      enabled: isOnScreen
    };
    $[19] = canvasBoxRef;
    $[20] = engineAnchorRef;
    $[21] = isOnScreen;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useEngineAnchor(t11);
  const t12 = isOnScreen && isEngineSettled;
  let t13;
  if ($[23] !== t12) {
    t13 = {
      areaRef: stageRef,
      elementRef: enginePointerRef,
      enabled: t12
    };
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  useEnginePointerParallax(t13);
  let t14;
  let t15;
  if ($[25] !== isAuraLit) {
    t14 = () => {
      const fade = animate(auraWriteRef.current, {
        value: isAuraLit ? 1 : 0
      }, {
        duration: isAuraLit ? SECTION_FADE_SECONDS : SECTION_ATMOSPHERE_FADE_OUT_SECONDS,
        ease: isAuraLit ? "easeOut" : "easeIn"
      });
      return () => fade.stop();
    };
    t15 = [isAuraLit];
    $[25] = isAuraLit;
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  useEffect(t14, t15);
  let t16;
  if ($[28] !== onAnimationEnd || $[29] !== onIntroVideoEnded) {
    t16 = () => {
      videoEndedRef.current = onIntroVideoEnded;
      animationEndRef.current = onAnimationEnd;
    };
    $[28] = onAnimationEnd;
    $[29] = onIntroVideoEnded;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  useEffect(t16);
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      unlock: isActiveNow => {
        if (isActiveNow) {
          playVideoFromGesture(videoElRef.current);
        } else {
          primeVideo(videoElRef.current);
        }
      },
      isClipOnHand: () => isClipResident(introClipSrc) || hasPlayableData(videoElRef.current)
    };
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  useHeroPlayUnlock(registerPlayUnlock, t17);
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = videoEl => {
      videoElRef.current = videoEl;
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const handleOnVideoElement = t18;
  let t19;
  if ($[33] !== onVideoReady) {
    t19 = videoEl_0 => {
      videoElRef.current = videoEl_0;
      setIsVideoIntroReady(true);
      safeCall(onVideoReady);
    };
    $[33] = onVideoReady;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const handleOnVideoReady = t19;
  let t20;
  if ($[35] !== onVideoError || $[36] !== startSettleAnimation) {
    t20 = error => {
      safeCall(onVideoError, error);
      if (startSettleAnimation) {
        return;
      }
      setStartSettleAnimation(true);
      videoEndedRef.current();
    };
    $[35] = onVideoError;
    $[36] = startSettleAnimation;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const handleOnVideoError = t20;
  let t21;
  let t22;
  if ($[38] !== isVideoIntroReady || $[39] !== startSettleAnimation) {
    t21 = () => {
      if (!isVideoIntroReady || startSettleAnimation) {
        return;
      }
      const videoEl_1 = videoElRef.current;
      if (!videoEl_1) {
        return;
      }
      let raf = 0;
      const runVideoProgressCheck = () => {
        if (videoEl_1.currentTime >= videoEl_1.duration * INTRO_VIDEO_END_PROGRESS) {
          setStartSettleAnimation(true);
          videoEndedRef.current();
          return;
        }
        raf = requestAnimationFrame(runVideoProgressCheck);
      };
      raf = requestAnimationFrame(runVideoProgressCheck);
      return () => {
        cancelAnimationFrame(raf);
      };
    };
    t22 = [isVideoIntroReady, startSettleAnimation];
    $[38] = isVideoIntroReady;
    $[39] = startSettleAnimation;
    $[40] = t21;
    $[41] = t22;
  } else {
    t21 = $[40];
    t22 = $[41];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[42] !== isReducedMotion || $[43] !== shouldPlayVideo || $[44] !== startSettleAnimation) {
    t23 = () => {
      const engine = engineRef.current;
      if (!engine) {
        return;
      }
      const slot = engineSlotRef.current;
      const videoBox = videoBoxRef.current;
      if (!shouldPlayVideo || !videoBox || !slot) {
        applyEngineFrameOffset(engine, {
          x: 0,
          y: 0,
          scale: 1
        }, 1);
        return;
      }
      const readIntroOffset = () => getEngineFrameOffset(videoBox.getBoundingClientRect(), slot.getBoundingClientRect(), ENGINE_INTRO_FRAME);
      if (!startSettleAnimation) {
        const track = () => applyEngineFrameOffset(engine, readIntroOffset(), 0);
        track();
        const observer = new ResizeObserver(track);
        observer.observe(videoBox);
        observer.observe(slot);
        return () => observer.disconnect();
      }
      const from = readIntroOffset();
      if (isReducedMotion) {
        applyEngineFrameOffset(engine, from, 1);
        setIsEngineSettled(true);
        return;
      }
      let isShowSettledUI = false;
      const controls = animate(0, 1, {
        duration: ENGINE_SETTLE_SECONDS,
        delay: ENGINE_SETTLE_DELAY_SECONDS,
        ease: ENGINE_SETTLE_EASE,
        onUpdate: t => {
          applyEngineFrameOffset(engine, from, t);
          if (!isShowSettledUI && t > 0.9) {
            isShowSettledUI = true;
            setIsEngineSettled(true);
          }
        }
      });
      return () => controls.stop();
    };
    t24 = [startSettleAnimation, shouldPlayVideo, isReducedMotion];
    $[42] = isReducedMotion;
    $[43] = shouldPlayVideo;
    $[44] = startSettleAnimation;
    $[45] = t23;
    $[46] = t24;
  } else {
    t23 = $[45];
    t24 = $[46];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[47] !== isEngineSettled) {
    t25 = () => {
      if (!isEngineSettled) {
        return;
      }
      animationEndRef.current();
    };
    t26 = [isEngineSettled];
    $[47] = isEngineSettled;
    $[48] = t25;
    $[49] = t26;
  } else {
    t25 = $[48];
    t26 = $[49];
  }
  useEffect(t25, t26);
  let t27;
  let t28;
  if ($[50] !== isEngineSettled) {
    t27 = () => {
      if (!isEngineSettled) {
        return;
      }
      const timer = window.setTimeout(() => setIsSpecValueReady(true), SPEC_VALUE_DELAY_MS);
      return () => window.clearTimeout(timer);
    };
    t28 = [isEngineSettled];
    $[50] = isEngineSettled;
    $[51] = t27;
    $[52] = t28;
  } else {
    t27 = $[51];
    t28 = $[52];
  }
  useEffect(t27, t28);
  let t29;
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className={cn("absolute top-0 left-0 h-60 w-full bg-linear-to-b from-black to-transparent")} />;
    t30 = <div className={cn(SECTION_VIDEO_BOX_CLASSNAME, "pointer-events-none")}><div className={cn("absolute inset-x-0 bottom-0", styles.introGradientBottom)} /></div>;
    $[53] = t29;
    $[54] = t30;
  } else {
    t29 = $[53];
    t30 = $[54];
  }
  let t31;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = cn("center-x lg:center-y aspect-1-1 absolute top-1/5 select-none", styles.introEngineSlot);
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== isEngineSettled || $[57] !== isReducedMotion || $[58] !== shouldRenderFallback) {
    t32 = !!shouldRenderFallback && <div className={cn("center-x center-y absolute size-[170%]", {
      [styles.introCoronaBreathing]: !isReducedMotion && isEngineSettled
    })}><AppImage src={BG_CORONA_IMAGE_SRC} alt="corona image" fill={true} sizes="(min-width: 1280px) 640px, 50vw" /></div>;
    $[56] = isEngineSettled;
    $[57] = isReducedMotion;
    $[58] = shouldRenderFallback;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== isActive || $[61] !== isReducedMotion || $[62] !== onFloatCycle) {
    t33 = event => {
      if (event.target === event.currentTarget && isActive && !isReducedMotion) {
        onFloatCycle?.();
      }
    };
    $[60] = isActive;
    $[61] = isReducedMotion;
    $[62] = onFloatCycle;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== isEngineSettled) {
    t34 = cn("absolute inset-0 origin-top-left transform-gpu", {
      [styles.engineFloat]: isEngineSettled
    });
    $[64] = isEngineSettled;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== onImageLoaded) {
    t35 = <div ref={enginePointerRef} className="absolute inset-0"><AppImage preload={true} fetchPriority="high" src={ENGINE_IMAGE_SRC_45_ANGLE} alt="ISUZU" width={1022} height={1022} objectFit="contain" className="aspect-1-1" sizes="60vw" onLoad={() => safeCall(onImageLoaded, "engine")} testId={SPARTAN_ENGINE_HERO_TRACKING.introEngine} /></div>;
    $[66] = onImageLoaded;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== t33 || $[69] !== t34 || $[70] !== t35) {
    t36 = <div ref={engineRef} onAnimationIteration={t33} className={t34}>{t35}</div>;
    $[68] = t33;
    $[69] = t34;
    $[70] = t35;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== t32 || $[73] !== t36) {
    t37 = <div ref={engineSlotRef} className={t31}>{t32}{t36}</div>;
    $[72] = t32;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== animateInSettledUiClassName) {
    t38 = cn("mt-2 flex justify-center lg:mt-0 lg:flex-col lg:justify-start", animateInSettledUiClassName);
    $[75] = animateInSettledUiClassName;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== onImageLoaded) {
    t39 = <div className="relative flex aspect-220/64 h-16 w-auto items-center lg:h-26"><AppImage src={ENGINE_LOGO_SRC} alt="Logo" fill={true} objectFit="contain" className="h-auto w-full" onLoad={() => safeCall(onImageLoaded, "logo")} testId={SPARTAN_ENGINE_HERO_TRACKING.introLogo} /></div>;
    $[77] = onImageLoaded;
    $[78] = t39;
  } else {
    t39 = $[78];
  }
  let t40;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <TextDescription variant="desktop" className="hidden lg:block" title={ENGINE_INTRO_SECTION_DATA.title} description={ENGINE_INTRO_SECTION_DATA.description} />;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== t38 || $[81] !== t39) {
    t41 = <div className={t38}>{t39}{t40}</div>;
    $[80] = t38;
    $[81] = t39;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  const t42 = !!animateInSettledUiClassName;
  let t43;
  if ($[83] !== animateInSettledUiClassName || $[84] !== t42) {
    t43 = cn(animateInSettledUiClassName, {
      "delay-200": t42
    });
    $[83] = animateInSettledUiClassName;
    $[84] = t42;
    $[85] = t43;
  } else {
    t43 = $[85];
  }
  let t44;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <TextDescription variant="mobile" className="mb-2 text-center lg:hidden" title={ENGINE_INTRO_SECTION_DATA.title} description={ENGINE_INTRO_SECTION_DATA.description} />;
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  let t45;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.introSpecCardList
    });
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  let t46;
  if ($[88] !== isSpecValueReady || $[89] !== shouldPlayVideo) {
    t46 = <div className="flex gap-4" {...t45}><EngineSpecCard value={ENGINE_SPEC.power.value} text={ENGINE_SPEC.power.label} isAnimate={isSpecValueReady} animateOnEnter={shouldPlayVideo} index={0} increasePercentage={ENGINE_SPEC.power.increasePercentage} className="w-full" /><EngineSpecCard value={ENGINE_SPEC.torque.value} text={ENGINE_SPEC.torque.label} isAnimate={isSpecValueReady} animateOnEnter={shouldPlayVideo} index={1} increasePercentage={ENGINE_SPEC.torque.increasePercentage} className="w-full" /></div>;
    $[88] = isSpecValueReady;
    $[89] = shouldPlayVideo;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] !== t43 || $[92] !== t46) {
    t47 = <div className={t43}>{t44}{t46}</div>;
    $[91] = t43;
    $[92] = t46;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] !== t41 || $[95] !== t47) {
    t48 = <Container className="flex grow flex-col justify-between pb-6 lg:flex-row lg:items-end lg:pb-10">{t41}{t47}</Container>;
    $[94] = t41;
    $[95] = t47;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] !== t37 || $[98] !== t48) {
    t49 = <div className="absolute inset-0 flex size-full flex-col items-center pt-14">{t29}{t30}{t37}{t48}</div>;
    $[97] = t37;
    $[98] = t48;
    $[99] = t49;
  } else {
    t49 = $[99];
  }
  let t50;
  if ($[100] !== handleOnVideoError || $[101] !== handleOnVideoReady || $[102] !== isActive || $[103] !== onImageLoaded || $[104] !== onVideoBlocked || $[105] !== onVideoPlaying || $[106] !== readyToPlay || $[107] !== shouldPlayVideo || $[108] !== startSettleAnimation) {
    t50 = !!shouldPlayVideo && <div ref={videoBoxRef} className={SECTION_VIDEO_BOX_CLASSNAME}><IntroVideo paused={!isActive || !readyToPlay} src={introClipSrc} coverSrc={ENGINE_INTRO_SECTION_DATA.videoSources.cover.desktop} coverAlt="Engine 2.2 Banner Logo" className={cn("ease-curve transition-opacity duration-2000", {
        "pointer-events-none opacity-0": startSettleAnimation
      })} onVideoReady={handleOnVideoReady} onVideoPlaying={onVideoPlaying} onVideoError={handleOnVideoError} onPlayBlocked={onVideoBlocked} onVideoElement={handleOnVideoElement} onCoverReady={() => safeCall(onImageLoaded, "videoCover")} /></div>;
    $[100] = handleOnVideoError;
    $[101] = handleOnVideoReady;
    $[102] = isActive;
    $[103] = onImageLoaded;
    $[104] = onVideoBlocked;
    $[105] = onVideoPlaying;
    $[106] = readyToPlay;
    $[107] = shouldPlayVideo;
    $[108] = startSettleAnimation;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== t49 || $[111] !== t50) {
    t51 = <div ref={stageRef} className="relative h-full w-full">{t49}{t50}</div>;
    $[110] = t49;
    $[111] = t50;
    $[112] = t51;
  } else {
    t51 = $[112];
  }
  return t51;
};
function _temp(entry) {
  return entry instanceof CSSAnimation && entry.animationName.includes("engineFloat");
}